import type { ReactElement } from 'react';
import { SYMBOL, SYMBOL_SMALL, SYMBOL_SMALL_BELOW } from './brand.generated';

export type MarkProps = {
  /** Lado en px. Por debajo de 32 cambia solo al maestro pequeño. */
  size?: number;
  /**
   * Texto accesible. `null` marca el símbolo como decorativo, que es lo correcto
   * cuando va al lado del logotipo: si no, un lector de pantalla dice «selflevel»
   * dos veces.
   */
  label?: string | null;
  className?: string;
};

/**
 * El símbolo: un disco cortado por un plano a 75° con las dos mitades deslizadas
 * a lo largo del corte. Una sola masa que se ha partido una vez.
 *
 * Tres reglas del paso 1 que este componente impone y no deja negociar:
 *
 *  1. **El portador de la identidad es el contorno.** Por eso `fill="currentColor"`
 *     y ninguna sombra, ningún degradado hacia el fondo.
 *  2. **Nunca dos colores, uno por mitad.** Es un solo `path` justamente para que
 *     eso sea imposible de escribir.
 *  3. Por debajo de 32 px va el maestro pequeño, sin excepción: el grande se
 *     cierra a ese tamaño.
 */
export function Mark({ size = 48, label = null, className }: MarkProps): ReactElement {
  const art = size < SYMBOL_SMALL_BELOW ? SYMBOL_SMALL : SYMBOL;
  const decorative = label === null;

  return (
    <svg
      viewBox={art.viewBox}
      width={size}
      height={size}
      className={className}
      {...(decorative
        ? { 'aria-hidden': true, focusable: false }
        : { role: 'img', 'aria-label': label })}
    >
      <path fill="currentColor" d={art.d} />
    </svg>
  );
}
